'use client';

import { useEffect } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { useEvent, usePrevious } from 'react-use';

import { CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { getActiveTtsContext, setActiveTtsContext, stopActiveTts } from '@/lib/tts/active-tts';
import { TTS_STOP_REASONS } from '@/lib/tts/analytics-constants';
import { sendAnalyticsBeacon } from '@/lib/utils/beacon';

// TTS teardown side-effects that the adapter can't observe on its own:
// - Thread switch: stop the active utterance (mirrors dictation behaviour).
// - Tab hidden: stop the active utterance (mirrors dictation behaviour).
// - Escape: stop the active utterance.
// - Pagehide: send a `tts_ended` beacon since the in-page event would be
//   killed by the page unload.
export function useTtsInterruption(): void {
  const threadId = useAuiState(state => state.threadListItem?.id);
  const prevThreadId = usePrevious(threadId);

  useEffect(() => {
    if (prevThreadId && prevThreadId !== threadId) {
      stopActiveTts(TTS_STOP_REASONS.THREAD_SWITCHED);
    }
  }, [threadId, prevThreadId]);

  useEvent('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      stopActiveTts(TTS_STOP_REASONS.TAB_HIDDEN);
    }
  });

  useEvent('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape') stopActiveTts();
  });

  useEvent('pagehide', () => {
    const ctx = getActiveTtsContext();
    if (!ctx) return;

    sendAnalyticsBeacon({
      event: CHAT_ANALYTICS_EVENTS.TTS_ENDED,
      properties: {
        ...ctx.staticProperties,
        ...ctx.getDynamicProperties(),
        durationMs: Date.now() - ctx.startTime,
        reason: TTS_STOP_REASONS.PAGE_NAVIGATION,
      },
    });
    setActiveTtsContext(null);
  });
}
